/**
 * Credit Card Validation Utilities
 * Kredi kartı validasyon fonksiyonları
 */

/**
 * Luhn Algorithm - Credit Card Number Validation
 * Kart numarasının geçerli olup olmadığını kontrol eder
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  // Sadece rakamları al (boşluk ve yıldızları temizle)
  const digits = cardNumber.replace(/\s/g, "").replace(/\*/g, "");

  // En az 13, en fazla 19 haneli olmalı
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  // Luhn algoritması
  let sum = 0;
  let isEven = false;

  // Sağdan sola doğru işle
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Get card brand from card number
 * Kart numarasından kart markasını belirler
 */
export const getCardBrand = (cardNumber: string): string | null => {
  const digits = cardNumber.replace(/\s/g, "").replace(/\*/g, "");

  if (!digits) return null;

  // Visa: 4 ile başlar
  if (digits.startsWith("4")) {
    return "visa";
  }

  // Mastercard: 5 ile başlar veya 2221-2720 arası
  if (digits.startsWith("5") || digits.startsWith("2")) {
    const firstFour = parseInt(digits.slice(0, 4), 10);
    if (digits.startsWith("5") || (firstFour >= 2221 && firstFour <= 2720)) {
      return "mastercard";
    }
  }

  // American Express: 34 veya 37 ile başlar
  if (digits.startsWith("34") || digits.startsWith("37")) {
    return "amex";
  }

  // Discover: 6011, 65, veya 644-649 ile başlar
  if (
    digits.startsWith("6011") ||
    digits.startsWith("65") ||
    (digits.length >= 3 &&
      parseInt(digits.slice(0, 3), 10) >= 644 &&
      parseInt(digits.slice(0, 3), 10) <= 649)
  ) {
    return "discover";
  }

  return null;
};

/**
 * Validate expiration date format and validity
 * Son kullanma tarihi formatını ve geçerliliğini kontrol eder
 */
export const validateExpirationDate = (
  expirationDate: string
): {
  isValid: boolean;
  error?: string;
} => {
  // Format: MM/YY
  if (!expirationDate || expirationDate.length < 5) {
    return { isValid: false, error: "INCOMPLETE" };
  }

  const parts = expirationDate.split("/");
  if (parts.length !== 2) {
    return { isValid: false, error: "INVALID_FORMAT" };
  }

  const month = parseInt(parts[0], 10);
  const year = parseInt(parts[1], 10);

  // Ay kontrolü (01-12)
  if (isNaN(month) || month < 1 || month > 12) {
    return { isValid: false, error: "INVALID_MONTH" };
  }

  // Yıl kontrolü (YY formatında, 00-99)
  if (isNaN(year) || year < 0 || year > 99) {
    return { isValid: false, error: "INVALID_YEAR" };
  }

  // Geçmiş tarih kontrolü
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // 1-12

  const fullYear = 2000 + year;

  if (fullYear < currentDate.getFullYear()) {
    return { isValid: false, error: "PAST_DATE" };
  }

  if (fullYear === currentDate.getFullYear() && month < currentMonth) {
    return { isValid: false, error: "PAST_DATE" };
  }

  return { isValid: true };
};

/**
 * Validate CVV code
 * CVV kodunu kontrol eder (genelde 3-4 haneli)
 */
export const validateCVV = (
  cvv: string,
  cardBrand?: string | null
): {
  isValid: boolean;
  error?: string;
} => {
  if (!cvv || cvv.length === 0) {
    return { isValid: false, error: "REQUIRED" };
  }

  const digits = cvv.replace(/\D/g, "");

  // Amex CID 4 hane; Visa / Mastercard / Discover CVC 3 hane.
  // 4. haneyi Visa'da kabul edip sağlayıcıya bırakmak, sağlayıcının
  // `invalid_cvc` dönmesine yol açıyordu.
  if (cardBrand === "amex") {
    if (digits.length !== 4) {
      return { isValid: false, error: "INVALID_LENGTH_AMEX" };
    }
  } else if (digits.length !== 3) {
    return { isValid: false, error: "INVALID_LENGTH" };
  }

  // Sadece rakamlardan oluşmalı
  if (!/^\d+$/.test(digits)) {
    return { isValid: false, error: "INVALID_FORMAT" };
  }

  return { isValid: true };
};

/**
 * Validate name on card
 * Kart üzerindeki ismi kontrol eder
 */
export const validateNameOnCard = (
  name: string
): {
  isValid: boolean;
  error?: string;
} => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "REQUIRED" };
  }

  // En az 2 karakter olmalı
  if (name.trim().length < 2) {
    return { isValid: false, error: "TOO_SHORT" };
  }

  // En fazla 50 karakter olmalı
  if (name.trim().length > 50) {
    return { isValid: false, error: "TOO_LONG" };
  }

  // En az iki harf içermeli. Hangi alfabede olduğuna bakılmaz (`\p{L}`):
  // eski Latin+Türkçe regex'i Arapça, Çince ve aksanlı isimleri reddediyordu.
  // Harfsiz bir değer ("1234") ise sağlayıcıdan `cardHolderName geçersiz`
  // olarak döner; onu buradan yakalıyoruz ki kart numarası boşuna yolculuk
  // yapmasın.
  if ((name.match(/\p{L}/gu) ?? []).length < 2) {
    return { isValid: false, error: "NO_LETTERS" };
  }

  return { isValid: true };
};

/**
 * Kart numarası alanının hata kodu — REQUIRED / INVALID_LENGTH / INVALID_NUMBER.
 * Üç form da aynı üç kuralı ayrı ayrı yazıyordu; tek yer burası.
 */
export const validateCardNumberField = (cardNumber: string): string | undefined => {
  const digits = cardNumber.replace(/\D/g, "");
  if (!digits) return "REQUIRED";
  if (digits.length < 13 || digits.length > 19) return "INVALID_LENGTH";
  if (!validateCardNumber(cardNumber)) return "INVALID_NUMBER";
  return undefined;
};

/**
 * Formdaki `AA/YY` → API'nin beklediği `expireMonth` / `expireYear`.
 *
 * `expireYear` HER YERDE dört hanedir. Kart ekleme (`POST /cards`) ve ödeme
 * (`ClientCheckoutDto.card`) aynı sağlayıcı alanına gider; sağlayıcı her iki
 * çağrıda da dört haneyi kabul ettiği kanıtlanmış tek biçimdir. Format kararı
 * yalnızca burada verilir — iki form birbirinden yeniden ayrışamaz.
 */
export const getExpiryParts = (
  expirationDate: string
): { expireMonth: string; expireYear: string } => {
  const [month = "", year = ""] = expirationDate.split("/");
  return { expireMonth: month.padStart(2, "0"), expireYear: `20${year}` };
};

/**
 * Kırpılmış alias, ya da hiç.
 *
 * Boş alias `""` olarak gönderilmez, alan tamamen atlanır: backend
 * `...(cardData.alias && …)` ile yayıyor, yani ikisi aynı anlama geliyor —
 * ama yalnızca eksik alan bunu dürüstçe söyler.
 */
export const getAliasField = (alias: string): { alias?: string } => {
  const trimmed = alias.trim();
  return trimmed ? { alias: trimmed } : {};
};

/**
 * Format card number with spaces (every 4 digits)
 * Kart numarasını 4 hanede bir boşluk ekleyerek formatlar
 */
export const formatCardNumber = (value: string): string => {
  // Sadece rakamları al
  const digits = value.replace(/\D/g, "");

  // 4 hanede bir boşluk ekle
  return digits.replace(/(.{4})/g, "$1 ").trim();
};

/**
 * Format card number and return formatted value with new cursor position
 * Kart numarasını formatlar ve cursor pozisyonunu korur
 */
export const formatCardNumberWithCursor = (
  value: string,
  cursorPosition: number
): { formattedValue: string; newCursorPosition: number } => {
  // Sadece rakamları al
  const digits = value.replace(/\D/g, "");

  // Eski cursor pozisyonuna göre kaç rakam olduğunu hesapla
  const beforeCursor = value.substring(0, cursorPosition);
  const digitsBeforeCursor = beforeCursor.replace(/\D/g, "").length;

  // Formatla
  const formatted = digits.replace(/(.{4})/g, "$1 ").trim();

  // Yeni cursor pozisyonunu hesapla
  // Formatlanmış string'de, cursor'un olduğu rakamın pozisyonunu bul
  let newCursorPos = 0;
  let digitCount = 0;

  // Eğer cursor başlangıçtaysa
  if (digitsBeforeCursor === 0) {
    newCursorPos = 0;
  } else {
    // Formatlanmış string'de ilgili pozisyonu bul
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== " ") {
        digitCount++;
      }

      // Eğer cursor'un olduğu rakamı bulduysak
      if (digitCount === digitsBeforeCursor) {
        // Cursor bu rakamdan sonra olmalı
        newCursorPos = i + 1;
        break;
      }
    }

    // Eğer tüm rakamların sonundaysa
    if (digitCount < digitsBeforeCursor) {
      newCursorPos = formatted.length;
    }
  }

  return { formattedValue: formatted, newCursorPosition: newCursorPos };
};

/**
 * Format expiration date as MM/YY
 * Son kullanma tarihini MM/YY formatında formatlar
 */
export const formatExpirationDate = (value: string): string => {
  // Sadece rakamları al
  const digits = value.replace(/\D/g, "");

  // 2 haneden sonra / ekle
  if (digits.length >= 2) {
    return digits.slice(0, 2) + "/" + digits.slice(2, 4);
  }

  return digits;
};

/**
 * Complete credit card validation
 * Tüm kredi kartı bilgilerini kontrol eder
 */
export interface CreditCardValidationResult {
  isValid: boolean;
  errors: {
    cardNumber?: string;
    expirationDate?: string;
    cvv?: string;
    nameOnCard?: string;
  };
}

export const validateCreditCard = (data: {
  cardNumber: string;
  expirationDate: string;
  cvv: string;
  nameOnCard: string;
}): CreditCardValidationResult => {
  const errors: CreditCardValidationResult["errors"] = {};
  let isValid = true;

  // Card Number Validation
  const cardNumberDigits = data.cardNumber
    .replace(/\s/g, "")
    .replace(/\*/g, "");
  if (!cardNumberDigits || cardNumberDigits.length === 0) {
    errors.cardNumber = "REQUIRED";
    isValid = false;
  } else if (cardNumberDigits.length < 13 || cardNumberDigits.length > 19) {
    errors.cardNumber = "INVALID_LENGTH";
    isValid = false;
  } else if (!validateCardNumber(data.cardNumber)) {
    errors.cardNumber = "INVALID_NUMBER";
    isValid = false;
  }

  // Get card brand for CVV validation
  const cardBrand = getCardBrand(data.cardNumber);

  // Expiration Date Validation
  const expirationValidation = validateExpirationDate(data.expirationDate);
  if (!expirationValidation.isValid) {
    errors.expirationDate = expirationValidation.error || "INVALID";
    isValid = false;
  }

  // CVV Validation
  const cvvValidation = validateCVV(data.cvv, cardBrand);
  if (!cvvValidation.isValid) {
    errors.cvv = cvvValidation.error || "INVALID";
    isValid = false;
  }

  // Name on Card Validation
  const nameValidation = validateNameOnCard(data.nameOnCard);
  if (!nameValidation.isValid) {
    errors.nameOnCard = nameValidation.error || "INVALID";
    isValid = false;
  }

  return { isValid, errors };
};
